/**
 * Coordinator for the on-canvas gizmos that appear when exactly one
 * OpeningAnnotation is selected in phase-1 of the DoorWindowTool.
 *
 * Owns four child gizmos (added in subsequent build steps):
 *   - two translate handles (one per side along the wall normal)
 *   - one rotation disk at the opening center
 *   - two "align parallel" DOM buttons (left/right neighbor wall)
 *   - one "align to average" DOM button (mean of neighbors)
 *
 * Lifecycle is driven by the parent `DoorWindowTool`:
 *   - `attach(annotation)` on entering the single-selection state.
 *   - `refresh()` after every geometry mutation so the gizmo positions
 *     follow the opening.
 *   - `setVisible(false)` while phase-2 wall focus is active (gizmos hide
 *     but state survives).
 *   - `detach()` on selection change, phase-2 enter that mutates the
 *     opening, floor change, or tool disable.
 *
 * Hit-testing and drag dispatch:
 *   - `pickAt(event)` returns a `GizmoHit` if any 3D handle is under the
 *     cursor (consulted by `onMouseDown` before background selection).
 *   - `beginDrag` / `onMouseMove` / `endDrag` drive the active drag; the
 *     controller pushes an `OpeningGeometrySnapshot` at `endDrag`.
 *
 * DOM align buttons handle their own clicks (they live outside the canvas);
 * they call `applyAlignParallel(side)` / `applyAlignAverage()` directly,
 * each of which pushes its own snapshot.
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { OpeningAnnotation } from '../../../../utils/PointoramaAnnotation';
import { PointoramaRoom } from '../../../../utils/PointoramaRoom';
import { PointoramaOutline } from '../../../../utils/PointoramaOutline';
import { ToolUndoRedoManager } from '../../ToolUndoRedoManager';
import { PixelHitTester } from '../selection/PixelHitTester';
import { computeOpeningFrame, computeNeighborDirections, OpeningFrame, NeighborDirections, rebuildPolygonWithU } from './OpeningFrame';
import { EdgeHighlight, EdgeSegment } from '../line-detection/EdgeHighlight';
import { OpeningTranslateHandle } from './OpeningTranslateHandle';
import { OpeningRotationHandle } from './OpeningRotationHandle';
import { OpeningGeometrySnapshot } from './OpeningGeometrySnapshot';
import { AlignParallelButtons, AlignSide } from './AlignParallelButtons';
import { AlignAverageButton } from './AlignAverageButton';
import { Utils } from '../../../../utils';

/**
 * Pixel offset from the opening center along ±normal where the parallel-
 * align buttons sit. Beyond the translate arrow's tip (arrows extend
 * ~195 px from center) so the button has breathing room from the
 * arrow + ring cluster.
 */
const PARALLEL_BUTTON_OFFSET_PX = 220;

/**
 * Pixel offset from the opening center along +u where the average-align
 * button sits. Beyond the rotation ring (outer 150 px) along the wall
 * axis. Only shown when BOTH parallel buttons are visible.
 */
const AVERAGE_BUTTON_OFFSET_PX = 200;

/**
 * Anything `pickAt` can return — child gizmos define their own concrete
 * hit shapes (sphere index, ring quadrant, etc.). The controller treats
 * them as opaque tokens; only the originating child reads its hit data
 * back in `beginDrag` / `onMouseMove`.
 */
export interface GizmoHit {
  /** Which child gizmo claims this hit — used to dispatch beginDrag. */
  source: 'translate' | 'rotation';
  /** Opaque payload, interpreted by the source child. */
  data: unknown;
}

export interface OpeningGizmoControllerDeps {
  readonly viewer: Viewer;
  /** Live undo manager from the outer DoorWindowTool. */
  readonly undoRedo: () => ToolUndoRedoManager | null;
  /** Rooms on the current floor, for neighbor-wall lookup. Read each
   *  call (not cached) because rooms can be edited or added at any
   *  time — `computeNeighborDirections` will reflect those changes on
   *  the next gizmo refresh, including during a drag. */
  readonly getRooms: () => PointoramaRoom[];
  /** Outlines on the current floor. Openings placed on an outline edge have
   *  no connected rooms, so neighbor-wall lookup falls back to outline edges. */
  readonly getOutlines: () => PointoramaOutline[];
}

export class OpeningGizmoController {
  private annotation: OpeningAnnotation | null = null;
  private frame: OpeningFrame | null = null;
  private neighbors: NeighborDirections = { posNormalDir: null, negNormalDir: null, avgDir: null, posNormalEdge: null, negNormalEdge: null };

  /**
   * Helpers group parented to `viewer.scene.scene`. All 3D child handles
   * attach here. Positioned at the opening center so child geometry is
   * authored in centered local coords (precision-safe at UTM scale, same
   * trick `WallSketchEditSession.ensureHelpersGroup` uses).
   */
  private helpersGroup: THREE.Group | null = null;

  /** Pixel-space hit tester shared across child gizmos. */
  private hitTester: PixelHitTester;

  /** Translate handles (positive-normal + negative-normal sides). */
  private translatePositive: OpeningTranslateHandle | null = null;
  private translateNegative: OpeningTranslateHandle | null = null;

  /** Rotation disk gizmo at the opening center. */
  private rotationHandle: OpeningRotationHandle | null = null;

  /** "Align parallel" DOM buttons (one per side). */
  private alignParallelButtons: AlignParallelButtons | null = null;

  /** "Align to average" DOM button. */
  private alignAverageButton: AlignAverageButton | null = null;

  /**
   * Cyan highlight of the neighbor walls the align buttons target — same look
   * as the LineTool edge-op alignment (shared `EdgeHighlight`). Rendered into
   * its own tool-overlay scene so it sits on top of the point cloud (like
   * `RoomEdgeHoverOverlay`), not buried by the EDL composite.
   */
  private edgeHighlight: EdgeHighlight | null = null;
  private edgeHighlightScene: THREE.Scene | null = null;
  private unregisterEdgeHighlightScene: (() => void) | null = null;

  /** Which child is currently driving a drag, if any. */
  private activeDrag: { source: 'translate'; side: 'positive' | 'negative' } | { source: 'rotation' } | null = null;

  /**
   * Flag set on a successful `pickAt` → `beginDrag`. Read by the outer
   * tool's `onClick` to skip the click body — without this, a no-drift
   * drag would fire a synthetic click that reapplies background selection
   * logic on top of the just-edited opening.
   */
  consumedThisInteraction = false;

  /** Whether a drag is currently in-flight. Outer tool's `onMouseMove`
   *  forwards to us only when this is true. */
  isDragging = false;

  /**
   * Bound per-frame update listener. The 3D handles (translate arrows,
   * rotation ring) wire their own per-frame layout via SphereMarker /
   * RingGeometry's `update` listeners. The DOM buttons need the same
   * — their world anchor depends on camera distance (pixel→world
   * conversion), so without a per-frame recompute they'd drift
   * visually as the user zooms.
   */
  private readonly boundOnViewerUpdate: () => void;

  constructor(private readonly deps: OpeningGizmoControllerDeps) {
    this.hitTester = new PixelHitTester(deps.viewer);
    this.boundOnViewerUpdate = () => this.updateButtonAnchors();
  }

  /**
   * Whether the controller is currently attached to an annotation. Used
   * by the outer tool to decide if `pickAt` is worth calling.
   */
  isAttached(): boolean {
    return this.annotation !== null;
  }

  /**
   * Bind the controller to a single annotation and build the helpers
   * group. Idempotent — calling with the same annotation just refreshes
   * positions; calling with a different one detaches the previous bind
   * first.
   */
  attach(annotation: OpeningAnnotation): void {
    if (this.annotation && this.annotation.identifier === annotation.identifier) {
      this.refresh();
      return;
    }
    this.detach();
    this.annotation = annotation;
    const group = this.ensureHelpersGroup();
    this.translatePositive = new OpeningTranslateHandle(this.deps.viewer, group, 'positive', this.hitTester);
    this.translateNegative = new OpeningTranslateHandle(this.deps.viewer, group, 'negative', this.hitTester);
    this.rotationHandle = new OpeningRotationHandle(this.deps.viewer, group, this.hitTester);
    this.alignParallelButtons = new AlignParallelButtons({
      viewer: this.deps.viewer,
      onClickSide: (side) => this.applyAlignParallel(side),
    });
    this.alignAverageButton = new AlignAverageButton({
      viewer: this.deps.viewer,
      onClick: () => this.applyAlignAverage(),
    });
    this.edgeHighlightScene = new THREE.Scene();
    this.unregisterEdgeHighlightScene = this.deps.viewer.registerToolOverlayScene(this.edgeHighlightScene);
    this.edgeHighlight = new EdgeHighlight(this.edgeHighlightScene);
    this.deps.viewer.addEventListener('update', this.boundOnViewerUpdate);
    this.refresh();
  }

  /**
   * Tear down child gizmos, remove the helpers group from the scene, and
   * clear all state. Safe to call when not attached.
   */
  detach(): void {
    if (this.isDragging) {
      // A drag in flight is implicitly cancelled — drop the consumed flag
      // so the next click doesn't get swallowed. We don't push a snapshot
      // because the live polygon already reflects the drag's last move
      // (cancelling cleanly would require restoring before-drag points,
      // not done in v1).
      this.isDragging = false;
    }
    this.consumedThisInteraction = false;
    this.activeDrag = null;
    this.translatePositive?.dispose();
    this.translatePositive = null;
    this.translateNegative?.dispose();
    this.translateNegative = null;
    this.rotationHandle?.dispose();
    this.rotationHandle = null;
    this.alignParallelButtons?.dispose();
    this.alignParallelButtons = null;
    this.alignAverageButton?.dispose();
    this.alignAverageButton = null;
    this.edgeHighlight?.dispose();
    this.edgeHighlight = null;
    this.unregisterEdgeHighlightScene?.();
    this.unregisterEdgeHighlightScene = null;
    this.edgeHighlightScene = null;
    this.deps.viewer.removeEventListener('update', this.boundOnViewerUpdate);
    if (this.helpersGroup) {
      this.deps.viewer.scene.scene.remove(this.helpersGroup);
      this.helpersGroup = null;
    }
    this.annotation = null;
    this.frame = null;
    this.neighbors = { posNormalDir: null, negNormalDir: null, avgDir: null, posNormalEdge: null, negNormalEdge: null };
  }

  /**
   * Recompute the opening frame + neighbor directions from the live
   * annotation, and reposition all child gizmos. Call after any geometry
   * mutation (drag, align click, undo, phase-2 round-trip).
   */
  refresh(): void {
    if (!this.annotation) return;
    // Defensive: if the bound annotation was removed from the scene
    // while attached (e.g. an undo deleted it), bail. The owning tool
    // will detach us via its selection-change observer shortly after.
    const stillInScene = this.deps.viewer.scene.measurements.some((m) => m === this.annotation);
    if (!stillInScene) return;
    this.frame = computeOpeningFrame(this.annotation);
    if (!this.frame) return;
    const rooms = this.deps.getRooms();
    this.neighbors = computeNeighborDirections(rooms, this.deps.getOutlines(), this.frame);
    // Highlight the neighbor walls the align buttons target — same cyan look as
    // the LineTool edge-op alignment.
    const highlightEdges: EdgeSegment[] = [];
    if (this.neighbors.posNormalEdge) highlightEdges.push({ start: this.neighbors.posNormalEdge.start, end: this.neighbors.posNormalEdge.end });
    if (this.neighbors.negNormalEdge) highlightEdges.push({ start: this.neighbors.negNormalEdge.start, end: this.neighbors.negNormalEdge.end });
    // Anchor the dashed connector at the opening center so it reads as
    // "this opening aligns to that wall" — same relationship line as the edge-op.
    this.edgeHighlight?.setEdges(highlightEdges, this.frame.center.clone());
    this.repositionHelpersGroup();
    if (this.helpersGroup) {
      this.translatePositive?.refresh(this.frame, this.helpersGroup.position);
      this.translateNegative?.refresh(this.frame, this.helpersGroup.position);
      this.rotationHandle?.refresh(this.frame, this.helpersGroup.position);
    }
    this.updateButtonAnchors();
  }

  /**
   * Recompute the DOM button anchors from the live frame + neighbors.
   * Separate from `refresh()` so it can be called per-frame from the
   * viewer's `update` event without re-walking room polygons. Without
   * this per-frame call, the buttons would stay anchored to a STALE
   * world position when the user zooms — the pixel→world conversion
   * depends on camera distance, so the anchor must be recomputed
   * whenever the camera moves.
   */
  private updateButtonAnchors(): void {
    if (!this.frame) return;
    const f = this.frame;
    const camera = this.deps.viewer.scene.getActiveCamera();
    const size = this.deps.viewer.renderer.getSize(new THREE.Vector2());
    const distance = camera.position.distanceTo(f.center);
    const projected = Utils.projectedRadius(1, camera, distance, size.width, size.height);
    const pxToWorld = projected > 1e-6 ? 1 / projected : 0;
    const parallelOffsetWorld = PARALLEL_BUTTON_OFFSET_PX * pxToWorld;
    const averageOffsetWorld = AVERAGE_BUTTON_OFFSET_PX * pxToWorld;

    if (this.alignParallelButtons) {
      const posAnchor = this.neighbors.posNormalDir ? f.center.clone().addScaledVector(f.normal, +parallelOffsetWorld) : null;
      const negAnchor = this.neighbors.negNormalDir ? f.center.clone().addScaledVector(f.normal, -parallelOffsetWorld) : null;
      this.alignParallelButtons.setAnchors(posAnchor, negAnchor);
    }
    if (this.alignAverageButton) {
      const showAverage = this.neighbors.posNormalDir !== null && this.neighbors.negNormalDir !== null && this.neighbors.avgDir !== null;
      const anchor = showAverage ? f.center.clone().addScaledVector(f.u, +averageOffsetWorld) : null;
      this.alignAverageButton.setAnchor(anchor);
    }
  }

  /**
   * Show or hide all gizmos without destroying state. Used by phase-2
   * enter/exit so the gizmos vanish during wall-focus drawing but
   * survive the round-trip with their position/state intact.
   */
  setVisible(visible: boolean): void {
    if (this.helpersGroup) this.helpersGroup.visible = visible;
    if (this.edgeHighlightScene) this.edgeHighlightScene.visible = visible;
    this.alignParallelButtons?.setVisible(visible);
    this.alignAverageButton?.setVisible(visible);
  }

  /**
   * Try to claim a mousedown event. Returns null if no handle is hit.
   * The outer tool calls this before its own selection logic so handle
   * picks take priority over wall / annotation re-selection.
   */
  pickAt(event: MouseEvent): GizmoHit | null {
    // Translate spheres take priority over the rotation ring — the
    // spheres are discrete handles the user clicks for "move", while
    // the ring is a wider region for "rotate". When the cursor is on
    // top of a sphere that's inside the ring annulus, the user almost
    // certainly meant to drag the sphere.
    const posHit = this.translatePositive?.pickAt(event);
    if (posHit) return { source: 'translate', data: posHit };
    const negHit = this.translateNegative?.pickAt(event);
    if (negHit) return { source: 'translate', data: negHit };
    if (this.rotationHandle && this.frame && this.rotationHandle.pickAt(event, this.frame)) {
      return { source: 'rotation', data: {} };
    }
    return null;
  }

  /**
   * True iff the cursor is currently over any gizmo handle. Read-only —
   * the outer tool uses this from `onMouseMove` to switch the cursor
   * and suppress wall-hover highlighting so the gizmo affordance reads
   * clearly. Returns false during a drag (the outer tool already
   * handles cursor state via `isDragging`).
   */
  isHoveredOverGizmo(event: MouseEvent): boolean {
    if (!this.isAttached()) return false;
    return this.pickAt(event) !== null;
  }

  /**
   * Start a drag against a previously-hit gizmo. The hit's `source` routes
   * to the right child's `onDragStart`. Sets `isDragging = true` and
   * `consumedThisInteraction = true` so the outer tool's click handler
   * skips the synthetic click that follows mouseup.
   */
  beginDrag(hit: GizmoHit, event: MouseEvent): void {
    if (!this.annotation || !this.frame) return;
    this.isDragging = true;
    this.consumedThisInteraction = true;
    if (hit.source === 'translate') {
      const data = hit.data as { side: 'positive' | 'negative' };
      const handle = data.side === 'positive' ? this.translatePositive : this.translateNegative;
      handle?.beginDrag(event, this.annotation, this.frame);
      this.activeDrag = { source: 'translate', side: data.side };
    } else if (hit.source === 'rotation') {
      this.rotationHandle?.beginDrag(event, this.annotation, this.frame);
      this.activeDrag = { source: 'rotation' };
    }
  }

  /**
   * Forward a mousemove to the active drag. No-op when not dragging.
   */
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging || !this.activeDrag || !this.annotation || !this.frame) return;
    if (this.activeDrag.source === 'translate') {
      const handle = this.activeDrag.side === 'positive' ? this.translatePositive : this.translateNegative;
      handle?.onMouseMove(event, this.annotation, this.frame);
    } else if (this.activeDrag.source === 'rotation') {
      this.rotationHandle?.onMouseMove(event, this.annotation);
    }
    // Refresh the cached frame + child gizmo positions per drag frame
    // so the arrows, rotation ring, and align buttons follow the live
    // polygon. Translate uses a frozen `dragBeforePoints` baseline and
    // rotation uses a frozen `dragStartFrame`, so refreshing the
    // PUBLIC frame here doesn't disturb either drag's internal math.
    this.refresh();
  }

  /**
   * End the active drag: push an `OpeningGeometrySnapshot` capturing the
   * polygon delta and dispatch `door_window_updated` so Apollo persists.
   * Called from the outer tool's `onMouseUp`.
   */
  endDrag(): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    if (this.activeDrag && this.annotation) {
      let result: { before: THREE.Vector3[]; after: THREE.Vector3[] } | null = null;
      if (this.activeDrag.source === 'translate') {
        const handle = this.activeDrag.side === 'positive' ? this.translatePositive : this.translateNegative;
        result = handle?.endDrag(this.annotation) ?? null;
      } else if (this.activeDrag.source === 'rotation') {
        result = this.rotationHandle?.endDrag(this.annotation) ?? null;
      }
      if (result) {
        // Skip no-op drags (drag-and-release without movement) so the
        // undo stack doesn't accumulate empty entries. Bind to a local
        // so the closure captures the narrowed reference without needing
        // a non-null assertion inside the arrow body.
        const { before, after } = result;
        const moved = before.some((p, i) => p.distanceToSquared(after[i]) > 1e-12);
        if (moved) {
          this.deps.undoRedo()?.pushState(new OpeningGeometrySnapshot(this.deps.viewer, this.annotation.identifier, before, after));
          // Persist to Apollo: mirrors `DoorWindowEditKindAdapter.commitEdit`'s
          // dispatch shape so the React listener writes the new polygon
          // to the reactive store + GraphQL.
          this.deps.viewer.scene.dispatchEvent({
            type: 'door_window_updated',
            annotationId: this.annotation.identifier,
            polygon: after.map((p) => ({ x: p.x, y: p.y, z: p.z })),
          });
        }
      }
    }
    this.activeDrag = null;
    this.refresh();
  }

  /**
   * Reset the `consumedThisInteraction` flag — the outer tool calls this
   * after its `onClick` has consulted it, so the next interaction starts
   * fresh.
   */
  clearConsumedFlag(): void {
    this.consumedThisInteraction = false;
  }

  /**
   * Build (or reuse) the helpers group and position it at the opening
   * center. Position-shifting lets child geometry use small local coords
   * to dodge float32 precision at UTM-scale world coords (same trick as
   * `WallSketchEditSession.ensureHelpersGroup`).
   */
  private ensureHelpersGroup(): THREE.Group {
    if (!this.helpersGroup) {
      this.helpersGroup = new THREE.Group();
      this.helpersGroup.name = 'opening_gizmos';
      this.deps.viewer.scene.scene.add(this.helpersGroup);
    }
    return this.helpersGroup;
  }

  /** Move the helpers group to follow the live opening center. */
  private repositionHelpersGroup(): void {
    if (!this.helpersGroup || !this.frame) return;
    this.helpersGroup.position.copy(this.frame.center);
  }

  /**
   * World-space point of a child gizmo, expressed relative to the helpers
   * group. Child gizmos use this so their geometry sits at small local
   * coords near the origin (float32-precision friendly).
   */
  worldToHelpersLocal(world: THREE.Vector3): THREE.Vector3 {
    if (!this.helpersGroup) return world.clone();
    return world.clone().sub(this.helpersGroup.position);
  }

  /** Live frame snapshot. Null when not attached or polygon is degenerate. */
  getFrame(): OpeningFrame | null {
    return this.frame;
  }

  /** Live neighbor directions. */
  getNeighbors(): NeighborDirections {
    return this.neighbors;
  }

  /** Live annotation. Null when not attached. */
  getAnnotation(): OpeningAnnotation | null {
    return this.annotation;
  }

  /**
   * Rotate the opening so its `u` axis is parallel to the chosen
   * neighbor's wall direction. Orientation is treated as undirected —
   * we pick whichever of `±neighborDir` is closer to the current `u` to
   * avoid a 180° flip (the opening's inside/outside is set by `normal`
   * direction, which we want to preserve).
   *
   * No-op when the neighbor on that side is null (dead-end wall) or when
   * the new orientation is within ~0.1° of the current — keeps the undo
   * stack uncluttered.
   */
  private applyAlignParallel(side: AlignSide): void {
    if (!this.annotation || !this.frame) return;
    const neighborDir = side === 'pos' ? this.neighbors.posNormalDir : this.neighbors.negNormalDir;
    if (!neighborDir) return;
    this.applyRotationToAxis(neighborDir);
  }

  /**
   * Rotate the opening's `u` axis to the mean direction of both neighbor
   * walls. No-op when avgDir is null (a neighbor is missing or the wall
   * is already straight; see `computeNeighborDirections`).
   */
  private applyAlignAverage(): void {
    if (!this.annotation || !this.frame || !this.neighbors.avgDir) return;
    this.applyRotationToAxis(this.neighbors.avgDir);
  }

  /**
   * Common path for the two align actions: rotate the opening so its `u`
   * axis becomes parallel to `targetDir`. Resolves the directional
   * ambiguity (parallel = ±) by picking whichever sign is closer to the
   * current `u`, then rebuilds the 4-corner polygon, dispatches the
   * persistence event, and pushes an undo snapshot.
   */
  private applyRotationToAxis(targetDir: THREE.Vector3): void {
    if (!this.annotation || !this.frame) return;
    const frame = this.frame;
    // Pick the sign that keeps `u` closest to its current direction —
    // avoids unnecessary 180° flips that would invert `normal` (and so
    // the opening's "inside/outside") with no visible UX benefit.
    const dot = frame.u.dot(targetDir);
    const newU = dot >= 0 ? targetDir.clone() : targetDir.clone().negate();
    // No-op when the rotation is below ~0.1° — keep the undo stack tidy.
    const cosAngle = Math.min(1, Math.max(-1, frame.u.dot(newU)));
    const angle = Math.acos(cosAngle);
    if (angle < 0.0017) return; // ~0.1°

    const before = this.annotation.points.map((p) => p.position.clone());
    const after = rebuildPolygonWithU(frame, newU);
    this.annotation.setPoints(after.map((position) => ({ position, pointCloudId: '' })));
    this.deps.viewer.scene.dispatchEvent({
      type: 'door_window_updated',
      annotationId: this.annotation.identifier,
      polygon: after.map((p) => ({ x: p.x, y: p.y, z: p.z })),
    });
    this.deps.undoRedo()?.pushState(new OpeningGeometrySnapshot(this.deps.viewer, this.annotation.identifier, before, after));
    this.refresh();
  }
}
